import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { getAnalyticsApiBaseUrl } from '../../../config/api';
import { fetchWithAuth } from '../../../utils/auth';
import { tooltipStyle } from '../types';

type Range = '8h' | '24h' | '30d';

interface Point {
  ts: string;                  // ISO datetime
  primary_conns?: number;
  primary_memMB?: number;
  primary_cachePct?: number;
  primary_totalGB?: number;
  primary_qps?: number;
  secondary_conns?: number;
  secondary_memMB?: number;
  secondary_cachePct?: number;
  secondary_totalGB?: number;
  secondary_lag?: number;
  arbiter_conns?: number;
  arbiter_memMB?: number;
  redis_memBytes?: number;
  redis_memPct?: number;
  redis_clients?: number;
  redis_hitRate?: number;
  redis_evicted?: number;
  redis_totalCmds?: number;
}

interface HistoryResp { range: Range; points: Point[]; }

// Disk total (per the data volumes — both 250 GB after today's extend)
const DISK_TOTAL_GB = 250;

const formatTs = (iso: string, range: Range): string => {
  const d = new Date(iso);
  if (range === '30d') return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
};

const RANGES: { key: Range; label: string }[] = [
  { key: '8h',  label: '8h' },
  { key: '24h', label: '24h' },
  { key: '30d', label: '30d' },
];

const ChartCard: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({ title, subtitle, children }) => (
  <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-4">
    <div className="flex items-baseline justify-between mb-2">
      <h4 className="text-sm font-semibold text-white">{title}</h4>
      {subtitle && <span className="text-xs text-gray-500">{subtitle}</span>}
    </div>
    <div style={{ height: 200 }}>{children}</div>
  </div>
);

const axisProps = {
  stroke: '#64748b',
  fontSize: 11,
  tickLine: false,
};

const DatabaseHistoryCharts: React.FC = () => {
  const [range, setRange] = useState<Range>('8h');
  const [points, setPoints] = useState<Point[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (r: Range) => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetchWithAuth(`${getAnalyticsApiBaseUrl()}/db/history?range=${r}`, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json: HistoryResp = await res.json();
      setPoints(json.points || []);
    } catch (e: any) {
      setError(e.message || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(range); }, [load, range]);
  useEffect(() => {
    const id = setInterval(() => load(range), 60_000); // refresh every minute
    return () => clearInterval(id);
  }, [load, range]);

  // Add display fields
  const data = useMemo(() => points.map(p => ({
    ...p,
    label: formatTs(p.ts, range),
    primary_diskPct: p.primary_totalGB ? Math.round((p.primary_totalGB / DISK_TOTAL_GB) * 1000) / 10 : undefined,
    secondary_diskPct: p.secondary_totalGB ? Math.round((p.secondary_totalGB / DISK_TOTAL_GB) * 1000) / 10 : undefined,
    primary_memGB: p.primary_memMB ? Math.round(p.primary_memMB / 102.4) / 10 : undefined,
    secondary_memGB: p.secondary_memMB ? Math.round(p.secondary_memMB / 102.4) / 10 : undefined,
    arbiter_memGB: p.arbiter_memMB ? Math.round(p.arbiter_memMB / 102.4) / 10 : undefined,
    redis_memGB: p.redis_memBytes ? Math.round(p.redis_memBytes / 1e8) / 10 : undefined,
  })), [points, range]);

  return (
    <div className="space-y-4">
      {/* Range selector */}
      <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl p-1 w-fit">
        {RANGES.map(r => (
          <button
            key={r.key}
            onClick={() => setRange(r.key)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${
              range === r.key
                ? 'bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            {r.label}
          </button>
        ))}
        <span className="text-xs text-gray-500 px-2">
          {data.length} samples · {loading ? 'refreshing…' : `auto-refresh 60s`}
        </span>
      </div>

      {error && <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-lg p-3">{error}</div>}

      {data.length === 0 && !loading && !error && (
        <div className="text-amber-400 text-sm bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
          No history yet. The sampler writes one snapshot per minute — give it a few minutes after deploy.
        </div>
      )}

      {/* MongoDB charts */}
      <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider pt-2">MongoDB</h3>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Connections" subtitle="current per member">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="label" {...axisProps} />
              <YAxis {...axisProps} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="primary_conns" name="primary" stroke="#10b981" dot={false} strokeWidth={2} />
              <Line type="monotone" dataKey="secondary_conns" name="secondary" stroke="#38bdf8" dot={false} strokeWidth={2} />
              <Line type="monotone" dataKey="arbiter_conns" name="arbiter" stroke="#a78bfa" dot={false} strokeWidth={1.5} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Disk Used %" subtitle={`of ${DISK_TOTAL_GB} GB total`}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="label" {...axisProps} />
              <YAxis {...axisProps} domain={[0, 100]} unit="%" />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="primary_diskPct" name="primary %" stroke="#10b981" fill="#10b98133" />
              <Area type="monotone" dataKey="secondary_diskPct" name="secondary %" stroke="#38bdf8" fill="#38bdf833" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Memory (RSS, GB)" subtitle="resident set size">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="label" {...axisProps} />
              <YAxis {...axisProps} unit=" GB" />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="primary_memGB" name="primary" stroke="#10b981" dot={false} strokeWidth={2} />
              <Line type="monotone" dataKey="secondary_memGB" name="secondary" stroke="#38bdf8" dot={false} strokeWidth={2} />
              <Line type="monotone" dataKey="arbiter_memGB" name="arbiter" stroke="#a78bfa" dot={false} strokeWidth={1.5} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="WT Cache Used %" subtitle="data members only">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="label" {...axisProps} />
              <YAxis {...axisProps} domain={[0, 100]} unit="%" />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="primary_cachePct" name="primary" stroke="#10b981" dot={false} strokeWidth={2} />
              <Line type="monotone" dataKey="secondary_cachePct" name="secondary" stroke="#38bdf8" dot={false} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Redis charts */}
      <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider pt-2">Redis (ElastiCache)</h3>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Memory (GB)" subtitle="used vs max">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="label" {...axisProps} />
              <YAxis {...axisProps} unit=" GB" />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="redis_memGB" name="used" stroke="#FF9C15" fill="#FF9C1533" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Clients" subtitle="connected client count">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="label" {...axisProps} />
              <YAxis {...axisProps} />
              <Tooltip contentStyle={tooltipStyle} />
              <Line type="monotone" dataKey="redis_clients" name="clients" stroke="#fbbf24" dot={false} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Hit Rate %" subtitle="cache effectiveness">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="label" {...axisProps} />
              <YAxis {...axisProps} domain={[0, 100]} unit="%" />
              <Tooltip contentStyle={tooltipStyle} />
              <Line type="monotone" dataKey="redis_hitRate" name="hit rate" stroke="#10b981" dot={false} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Evicted Keys (cumulative)" subtitle="LRU evictions">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="label" {...axisProps} />
              <YAxis {...axisProps} />
              <Tooltip contentStyle={tooltipStyle} />
              <Line type="monotone" dataKey="redis_evicted" name="evicted (total)" stroke="#f87171" dot={false} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
};

export default DatabaseHistoryCharts;
